import { useState } from 'react';
import { Table, Button, Modal, Form, Input, Popconfirm, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useClients } from '../../hooks/useClients';
import dayjs from 'dayjs';
import { FilterDrama } from '@mui/icons-material';

const Clients = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [form] = Form.useForm();

  const { useGetClients, useAddClient, useUpdateClient, useDeleteClient } =
    useClients();

  const { data, isLoading } = useGetClients();
  const filteredData = (data || [])
    .filter(client => client?.is_archive === false)
    .sort((a, b) => new Date(b?.on) - new Date(a?.on));

  const addClient = useAddClient();
  const updateClient = useUpdateClient();
  const deleteClient = useDeleteClient();

  const openAddModal = () => {
    setEditingClient(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const openEditModal = record => {
    setEditingClient(record);
    form.setFieldsValue(record);
    setIsModalVisible(true);
  };

  const handleDelete = id => {
    deleteClient.mutate(id, {
      onSuccess: () => message.success('Client deleted'),
      onError: () => message.error('Failed to delete'),
    });
  };

  const handleSubmit = values => {
    const payload = {
      name: values.name || null,
      address: values.address || null,
      phone: values.phone || null,
      email: values.email || null,
      country: values.country || null,
      logo: null,
      status: null,
      on: dayjs().toISOString(),
      by: null,
      is_archive: false,
      org: 1,
    };

    if (editingClient) {
      updateClient.mutate(
        { id: editingClient.id, data: payload },
        {
          onSuccess: () => {
            message.success('Client updated');
            form.resetFields();
            setIsModalVisible(false);
          },
          onError: () => message.error('Failed to update client'),
        }
      );
    } else {
      addClient.mutate(payload, {
        onSuccess: () => {
          message.success('Client added');
          form.resetFields();
          setIsModalVisible(false);
        },
        onError: () => message.error('Failed to add client'),
      });
    }
  };

  const columns = [
    {
      title: 'Client Name',
      dataIndex: 'name',
      key: 'name',
      render: text => text || 'N/A',
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      render: text => text || '—',
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      render: text => text || '—',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: text => text || '—',
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      render: text => text || '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <>
          <Button
            icon={<EditOutlined />}
            style={{ marginRight: 8 }}
            onClick={() => openEditModal(record)}
          />
          <Popconfirm
            title="Are you sure to delete this client?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <h2>Clients</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>
          Add Client
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={filteredData}
        rowKey={record => record.id}
        loading={isLoading}
        pagination={{ pageSize: 5 }}
      />

      <Modal
        title={editingClient ? 'Edit Client' : 'Add New Client'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={() => form.submit()}
        okText={editingClient ? 'Update' : 'Add'}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Client Name"
            rules={[
              { required: true, message: 'Please enter the client name' },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="address" label="Address">
            <Input />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Phone Number"
            rules={[{ required: true, message: 'Please enter a phone number' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[{ type: 'email', message: 'Enter a valid email' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="country" label="Country">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Clients;
